/**
 * ERC Single Pin Net Validator Adapter for DRC System
 *
 * This validator checks for nets that have only a single pin connected.
 * Logic enumerates nets first; each such net is reported as its own problem
 * (one row per net in the review UI).
 *
 * Feature-gated via DrcEnabledCheckers.ErcSinglePinNetValidator
 */
import { ReviewType } from "@buildwithflux/models";
import { getProblemTypeKeyFromReviewType } from "../../drc/types";
import { getPinTypeName } from "../pinTypes";
import { ErcSeverity, ElectricalPinType } from "../types";
import { createErcProblem, formatPinReference, getPinsForNet } from "./ercUtils";
/**
 * Validator that checks for nets with only a single pin.
 * Reports every net that has exactly one connected pin (no-connect pins excluded).
 */
export class ErcSinglePinNetValidator {
    reviewType = ReviewType.DesignRuleCheckErcSinglePinNet;
    problemTypeKey = getProblemTypeKeyFromReviewType(this.reviewType);
    defaultHidden = false;
    async checkForProblems(inputs) {
        const foundProblems = [];
        // Enumerate nets first; report each net that has only one pin
        for (const [netHash, terminals] of Object.entries(inputs.netMap)) {
            const pins = getPinsForNet(inputs, terminals);
            if (pins.length !== 1) {
                continue;
            }
            const pin = pins[0];
            // Skip no-connect pins - they're expected to be alone
            if (pin.pinType === ElectricalPinType.NO_CONNECT) {
                continue;
            }
            const netName = inputs.netNameByNetId?.[netHash] ?? netHash;
            const typeName = getPinTypeName(pin.pinType);
            const message = `Net "${netName}" has only one pin connected: ${formatPinReference(pin)} (${typeName}).`;
            // Affected item is the net; store terminal in metadata so the UI can link to the component
            const problem = createErcProblem(this.problemTypeKey, `${this.problemTypeKey}_${netHash}`, message, [netHash], [netName], ErcSeverity.WARNING);
            problem.metadata = {
                ...problem.metadata,
                ercNet: netName,
                ercTerminalId: pin.terminalId,
                ercPinLabel: formatPinReference(pin),
            };
            foundProblems.push(problem);
        }
        return {
            error: false,
            problemTypeKey: this.problemTypeKey,
            foundProblems,
        };
    }
}
//# sourceMappingURL=ErcSinglePinNetValidator.js.map